import React, { useEffect, useState } from 'react';
import Collapse from '@material-ui/core/Collapse';
import CircleRadio from '../Controls/Buttons/CircleRadio/CircleRadio';
import SquareRadio from '../Controls/Buttons/SquareRadio/SquareRadio';
import ADEntitiesSelector from '../Controls/Modals/ADEntitiesSelector/ADEntitiesSelector';
import ADServersSelector from '../Controls/Modals/ADServersSelector/ADServersSelector';
import IpsSelector from '../Controls/Modals/IpsSelector/IpsSelector';
import PolicyField from './PolicyField';

import { entitiesToSpans, uppercaseFirstLetter } from '../utils';
import { changeTokensAllowed, getAllowedTokensOptions } from './PoliciesUtils';
import { POLICY_TYPES, RISK_BY_TYPES } from './PoliciesConsts';
import {
    ACTIVE_DIRECTORY_PROTECT,
    AD_ENTITY_TYPES,
    DEFAULT_MFA_PROMPT,
    DEFAULT_MFA_PROMPT_KERBEROS,
    DEFAULT_MFA_PROMPT_NO_SOURCE,
    DEFAULT_VENDOR_TO_ID,
    DIRECTORY_TYPES,
    ENABLED_PROTOCOLS_TO_PROTECT,
    FEATURE_FLAGS_NAMES,
    MFA_TYPES,
    OKTA_SIGNIN_APP,
    POLICY_ACTION_TYPES,
    POLICY_MATCHING_BY_OPTIONS,
    POLICY_MODE_TYPES,
    POLICY_SELECT_ACCOUNT_OPTIONS,
    POLICY_SELECT_DESTINATION_OPTIONS,
    RELYING_PARTIES_TYPES,
    RETROSPECTIVE_VALIDATION_OPTIONS,
    RISK_INDICATORS_SELECTION_TYPE,
    RISK_LEVELS,
    SELECTOR_TYPES,
    SSO_APP_SOURCE_TYPE
} from '@silverfort-nodelibs/common/utils/consts';

import styles from './CssModules/PolicyRowMainBody.module.css';
import fieldStyles from './CssModules/PolicyField.module.css';

import { orderBy } from 'lodash/collection';
import { splitAndTransformEntities } from '@silverfort-nodelibs/common/utils/common';
import useFeature from '../SystemSettings/hooks/featureFlags/useFeature';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import {
    RISK_INDICATORS,
    RISK_INDICATORS_FOR_POLICY_LIST,
    RISK_RULES
} from '@silverfort-nodelibs/common/utils/insight';
import { allComputersResource, unresolvedResources } from './common';
import PolicySelectField from './components/PolicySelectField';
import { usePricingModulesState } from '../../context/contexts/PricingModuleContext';
import {
    getActionsOptions,
    getAllowedTokenTypes,
    getPolicyEntitiesModelSelectorType,
    getPolicyTypeOptions,
    isS4wlPolicyBodyData
} from './utils/utils';
import IncludingUsersCheckboxes from './components/IncludingUsersCheckboxes';

const RISK_POLICY_TYPE_OPTIONS = {
    AUTHENTICATION: { value: 'authentication', label: 'Authentication' },
    USER: { value: 'user', label: 'User' },
    RESOURCE: { value: 'resource', label: 'Resource' },
    SOURCE: { value: 'source', label: 'Source' }
};

const AUTHENTICATION_RISK_LEVEL_OPTIONS = Object.values(RISK_LEVELS)
    .filter((riskLevel) => {
        return riskLevel.value !== RISK_LEVELS.LOW.value;
    })
    .map((riskLevel) => {
        return {
            ...riskLevel,
            label:
                riskLevel.value !== RISK_LEVELS.CRITICAL.value
                    ? `${uppercaseFirstLetter(riskLevel.label)} or above`
                    : uppercaseFirstLetter(riskLevel.label)
        };
    });

const USER_RISK_LEVEL_OPTIONS = {
    HIGH: {
        value: 'high',
        label: uppercaseFirstLetter('high or above'),
        riskRules: [RISK_RULES.HIGH_RISK_USER, RISK_RULES.CRITICAL_RISK_USER]
    },
    CRITICAL: { value: 'critical', label: uppercaseFirstLetter('critical'), riskRules: [RISK_RULES.CRITICAL_RISK_USER] }
};

const RESOURCE_RISK_LEVEL_OPTIONS = {
    HIGH: {
        value: 'high',
        riskRules: [RISK_RULES.HIGH_RISK_RESOURCE, RISK_RULES.CRITICAL_RISK_RESOURCE],
        label: uppercaseFirstLetter('high or above')
    },
    CRITICAL: {
        value: 'critical',
        riskRules: [RISK_RULES.CRITICAL_RISK_RESOURCE],
        label: uppercaseFirstLetter('critical')
    }
};

const SOURCE_RISK_LEVEL_OPTIONS = {
    HIGH: {
        value: 'high',
        riskRules: [RISK_RULES.HIGH_RISK_SOURCE, RISK_RULES.CRITICAL_RISK_SOURCE],
        label: uppercaseFirstLetter('high or above')
    },
    CRITICAL: {
        value: 'critical',
        riskRules: [RISK_RULES.CRITICAL_RISK_SOURCE],
        label: uppercaseFirstLetter('critical')
    }
};

const getRiskPolicyTypeFromRiskIndicators = (riskIndicators) => {
    if (riskIndicators?.includes(RISK_RULES.CRITICAL_RISK_USER)) {
        return RISK_POLICY_TYPE_OPTIONS.USER.value;
    } else if (riskIndicators?.includes(RISK_RULES.CRITICAL_RISK_RESOURCE)) {
        return RISK_POLICY_TYPE_OPTIONS.RESOURCE.value;
    } else if (riskIndicators?.includes(RISK_RULES.CRITICAL_RISK_SOURCE)) {
        return RISK_POLICY_TYPE_OPTIONS.SOURCE.value;
    }
    return RISK_POLICY_TYPE_OPTIONS.AUTHENTICATION.value;
};

const PolicyRowMainBody = ({
    policyBodyData,
    setPolicyBodyData,
    policyConfiguration,
    onFieldChanged,
    toggleShowAdvancedOptions,
    setIsDirty,
    isEditor,
    selectAllApplications,
    setSelectAllApplications
}) => {
    const { pricingModulesState } = usePricingModulesState();
    const externalMfaProviders = policyConfiguration.externalMfaProviders ?? [];

    const disabledRadius = policyConfiguration.RadiusClients.length === 0;
    const disabledRelyingPartied = Object.keys(policyConfiguration.RelyingParties).length === 0;
    const disabledRelyingPartiedPingFed = Object.keys(policyConfiguration.RelyingPartiesPingFed).length === 0;

    const [riskPolicyType, setRiskPolicyType] = useState(
        getRiskPolicyTypeFromRiskIndicators(policyBodyData.risk_indicators)
    );

    // Show modals
    const [showEntitiesSelectorModal, setShowEntitiesSelectorModal] = useState(false);
    const [showHostsSelectorModal, setShowHostsSelectorModal] = useState(false);
    const [showDesktopDestinationSelectorModal, setShowDesktopDestinationSelectorModal] = useState(false);
    const [showHostsIpsSelectorModal, setShowHostsIpsSelectorModal] = useState(false);
    const [showServersSelectorModal, setShowServersSelectorModal] = useState(false);
    const [showServersIpsSelectorModal, setShowServersIpsSelectorModal] = useState(false);
    const [showApplicationSelectorModal, setShowApplicationSelectorModal] = useState(false);

    const [oktaAppsOptions, setOktaAppsOptions] = useState(false);
    const [accountTypes, setAccountTypes] = useState(
        policyBodyData?.account_types || [POLICY_SELECT_ACCOUNT_OPTIONS.USERS]
    );

    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const isSaas = systemMode !== SYSTEM_MODES.ON_PREM;
    const authTypesToHide = isSaas
        ? [
              ENABLED_PROTOCOLS_TO_PROTECT.ping_fed_enabled.value,
              ENABLED_PROTOCOLS_TO_PROTECT.radius_enabled.value,
              ENABLED_PROTOCOLS_TO_PROTECT.relying_parties_enabled.value
          ]
        : [];

    const isS4wlAuthType = isS4wlPolicyBodyData(policyBodyData);

    useEffect(() => {
        const oktaApps = { ...policyConfiguration.OktaApps };
        delete oktaApps[OKTA_SIGNIN_APP.identifier];

        const oktaOrderedApps = orderBy(Object.values(oktaApps), [(oktaApp) => oktaApp.display_name.toLowerCase()]);
        oktaOrderedApps.unshift(OKTA_SIGNIN_APP);

        if (!oktaAppsOptions || oktaAppsOptions.length !== oktaOrderedApps.length) {
            setOktaAppsOptions(
                oktaOrderedApps.map((okatApp) => {
                    return {
                        label: `${okatApp.display_name}${
                            okatApp.name && okatApp.display_name !== okatApp.name ? ` (${okatApp.name})` : ''
                        }`,
                        value: okatApp.identifier
                    };
                })
            );
        }
    }, [policyConfiguration.OktaApps, oktaAppsOptions]);

    const removeOriginalTGTFromMatchingBy = (policyData) => {
        if (
            (!policyData.kerberos_enabled || (policyData.kerberos_enabled && policyData.ntlm_enabled)) &&
            policyData.matching_by &&
            policyData.matching_by.includes(POLICY_MATCHING_BY_OPTIONS.TGT.value)
        ) {
            const tgtIndex = policyData.matching_by.findIndex(
                (option) => option === POLICY_MATCHING_BY_OPTIONS.TGT.value
            );
            policyData.matching_by.splice(tgtIndex, 1);
        }
    };

    const setDefaultMFAPrompt = (policyData) => {
        if (policyData.oidc_enabled || policyData.okta_enabled || policyData.ntlm_enabled) {
            if (
                policyData.display_name === DEFAULT_MFA_PROMPT_NO_SOURCE ||
                policyData.display_name === DEFAULT_MFA_PROMPT_KERBEROS
            ) {
                policyData.display_name = DEFAULT_MFA_PROMPT;
            }
        } else if (policyData.kerberos_enabled) {
            if (
                policyData.display_name === DEFAULT_MFA_PROMPT ||
                policyData.display_name === DEFAULT_MFA_PROMPT_NO_SOURCE
            ) {
                policyData.display_name = DEFAULT_MFA_PROMPT_KERBEROS;
            }
        } else if (
            policyData.display_name === DEFAULT_MFA_PROMPT ||
            policyData.display_name === DEFAULT_MFA_PROMPT_KERBEROS
        ) {
            policyData.display_name = DEFAULT_MFA_PROMPT_NO_SOURCE;
        }
    };

    const onProtectedTypeChange = (event) => {
        const newProtectTypeValue = parseInt(event.target.value);
        const newPolicyBodyData = { ...policyBodyData };
        for (const protectType in ENABLED_PROTOCOLS_TO_PROTECT) {
            if (protectType === 'ldap_enabled') {
                newPolicyBodyData[protectType] = false;
            } else {
                newPolicyBodyData[protectType] =
                    ENABLED_PROTOCOLS_TO_PROTECT[protectType].value === newProtectTypeValue;
            }
        }
        if (newPolicyBodyData.desktop_enabled) {
            let newAllowedTokens = [...newPolicyBodyData.allowed_token_types];

            const fidoTokenIndex = newAllowedTokens.findIndex((token) => token === MFA_TYPES.HARDWARE.ID);
            if (fidoTokenIndex >= 0) {
                newAllowedTokens.splice(fidoTokenIndex, 1);
            }

            const chromeTokenIndex = newAllowedTokens.findIndex((token) => token === MFA_TYPES.CHROME.ID);
            if (chromeTokenIndex >= 0) {
                newAllowedTokens.splice(chromeTokenIndex, 1);
            }

            const desktopAppTokenIndex = newAllowedTokens.findIndex((token) => token === MFA_TYPES.DESKTOP_APP.ID);
            if (desktopAppTokenIndex >= 0) {
                newAllowedTokens.splice(desktopAppTokenIndex, 1);
            }

            const desktopOtpTokenIndex = newAllowedTokens.findIndex((token) => token === MFA_TYPES.OTP.ID);
            if (newPolicyBodyData.mode_type === POLICY_MODE_TYPES.OFFLINE.value) {
                newAllowedTokens = [MFA_TYPES.OTP.ID, MFA_TYPES.HARDWARE.ID];
            } else if (desktopOtpTokenIndex < 0) {
                newAllowedTokens.unshift(MFA_TYPES.OTP.ID);
            }

            newPolicyBodyData.allowed_token_types = newAllowedTokens;
        }

        if (newProtectTypeValue !== ENABLED_PROTOCOLS_TO_PROTECT.ping_fed_enabled.value) {
            const newAllowedTokens = [...newPolicyBodyData.allowed_token_types];
            const pingFedTokenIndex = newAllowedTokens.findIndex((token) => token === MFA_TYPES.PING_ADAPTER.ID);
            if (pingFedTokenIndex >= 0) {
                newAllowedTokens.splice(pingFedTokenIndex, 1);
            }
            newPolicyBodyData.allowed_token_types = newAllowedTokens;
        }

        if (newProtectTypeValue !== ENABLED_PROTOCOLS_TO_PROTECT.radius_enabled.value) {
            const newAllowedTokens = [...newPolicyBodyData.allowed_token_types];
            const radiusTokenIndex = newAllowedTokens.findIndex((token) => token === MFA_TYPES.RADIUS_OTP.ID);
            if (radiusTokenIndex >= 0) {
                newAllowedTokens.splice(radiusTokenIndex, 1);
            }
            newPolicyBodyData.allowed_token_types = newAllowedTokens;
        }

        newPolicyBodyData.relying_parties = [];
        newPolicyBodyData.cloud_apps = [];

        if (
            newProtectTypeValue === ENABLED_PROTOCOLS_TO_PROTECT.kerberos_enabled.value ||
            newProtectTypeValue === ENABLED_PROTOCOLS_TO_PROTECT.ntlm_enabled.value
        ) {
            newPolicyBodyData.destination_types = [POLICY_SELECT_DESTINATION_OPTIONS.SERVERS];
        } else {
            newPolicyBodyData.destination_types = [];
        }
        newPolicyBodyData.unresolved_resources_match_all_services = true;
        newPolicyBodyData.unresolved_resources_services = [];

        setSelectAllApplications(
            newPolicyBodyData.okta_enabled ||
                newPolicyBodyData.relying_parties_enabled ||
                newPolicyBodyData.ping_fed_enabled
        );

        setDefaultMFAPrompt(newPolicyBodyData);
        removeOriginalTGTFromMatchingBy(newPolicyBodyData);
        setPolicyBodyData(newPolicyBodyData);
        setIsDirty(true);
    };

    const onProtocolChange = (event, protocol) => {
        event.preventDefault();
        const newPolicyBodyData = { ...policyBodyData };
        if (protocol === 'ldap') {
            newPolicyBodyData.ldap_enabled = true;
            newPolicyBodyData.kerberos_enabled = false;
            newPolicyBodyData.ntlm_enabled = false;
            newPolicyBodyData.destination_types = [];
            newPolicyBodyData.unresolved_resources_match_all_services = true;
            newPolicyBodyData.unresolved_resources_services = [];
        } else {
            newPolicyBodyData.ldap_enabled = false;
            if (!newPolicyBodyData.destination_types || newPolicyBodyData.destination_types.length === 0) {
                newPolicyBodyData.destination_types = [POLICY_SELECT_DESTINATION_OPTIONS.SERVERS];
            } else if (!newPolicyBodyData.destination_types.includes(POLICY_SELECT_DESTINATION_OPTIONS.SERVERS)) {
                newPolicyBodyData.destination_types.push(POLICY_SELECT_DESTINATION_OPTIONS.SERVERS);
            }

            if (protocol === 'kerberos') {
                if (!policyBodyData.kerberos_enabled || policyBodyData.ntlm_enabled) {
                    newPolicyBodyData.kerberos_enabled = !policyBodyData.kerberos_enabled;
                }
            }
            if (protocol === 'ntlm') {
                if (!policyBodyData.ntlm_enabled || policyBodyData.kerberos_enabled) {
                    newPolicyBodyData.ntlm_enabled = !policyBodyData.ntlm_enabled;
                }
            }
        }
        setDefaultMFAPrompt(newPolicyBodyData);
        removeOriginalTGTFromMatchingBy(newPolicyBodyData);
        setPolicyBodyData(newPolicyBodyData);
        setIsDirty(true);
    };

    const onModeTypeChange = (event) => {
        const newModeType = parseInt(event);
        const newPolicyBodyData = { ...policyBodyData };
        if (newModeType === POLICY_MODE_TYPES.OFFLINE.value) {
            newPolicyBodyData.allowed_token_types = [MFA_TYPES.OTP.ID];
        }
        newPolicyBodyData.mode_type = newModeType;
        setPolicyBodyData(newPolicyBodyData);
        setIsDirty(true);
    };

    const onServiceChange = (event, service) => {
        event.preventDefault();
        if (policyBodyData.desktop_services.length === 1 && policyBodyData.desktop_services.includes(service)) {
            return;
        }
        const newPolicyBodyData = { ...policyBodyData };
        const serviceIndex = policyBodyData.desktop_services.findIndex((desktopService) => desktopService === service);
        if (serviceIndex < 0) {
            policyBodyData.desktop_services.push(service);
        } else {
            policyBodyData.desktop_services.splice(serviceIndex, 1);
        }

        setPolicyBodyData(newPolicyBodyData);
        setIsDirty(true);
    };

    const onPolicyTypeChange = (event) => {
        onFieldChanged('risk_enabled', parseInt(event) === POLICY_TYPES.RISK_BASED.value);
    };

    const onRiskTypeChange = (event) => {
        if (
            riskPolicyType === RISK_POLICY_TYPE_OPTIONS.AUTHENTICATION.value &&
            parseInt(event.target.value) === RISK_BY_TYPES.LEVEL.value
        ) {
            onFieldChanged('risk_by_level', true);
            onFieldChanged('risk_indicators', []);
        } else {
            onFieldChanged('risk_by_level', false);
            setRiskPolicyType(RISK_POLICY_TYPE_OPTIONS.AUTHENTICATION.value);
        }
    };

    const onRiskIndicatorsChanged = (event) => {
        onFieldChanged('risk_indicators', event);
    };

    const onRadiusClientChange = (event) => {
        const newRadiusClients = [];
        for (const ipAddress of event) {
            if (ipAddress === '') {
                continue;
            }
            newRadiusClients.push({ ip_address: ipAddress });
        }
        onFieldChanged('radius_clients', newRadiusClients);
    };
    const onActionTypeChange = (event) => {
        const newPolicyBodyData = { ...policyBodyData };

        if (parseInt(event) !== POLICY_ACTION_TYPES.MFA.value) {
            toggleShowAdvancedOptions(false);
            newPolicyBodyData.enforce_location_if_na = false;
        } else {
            newPolicyBodyData.enforce_location_if_na = true;
            if (newPolicyBodyData.allowed_token_types) {
                newPolicyBodyData.allowed_token_types = newPolicyBodyData.allowed_token_types.filter(
                    (tokenType) => tokenType !== MFA_TYPES.AZURE_SSO.ID
                );
            }
        }

        if (parseInt(event) === POLICY_ACTION_TYPES.BRIDGE.value) {
            newPolicyBodyData.risk_enabled = false;
            if (newPolicyBodyData.oidc_enabled || newPolicyBodyData.desktop_enabled) {
                newPolicyBodyData.oidc_enabled = false;
                newPolicyBodyData.desktop_enabled = false;
                newPolicyBodyData.kerberos_enabled = true;
                newPolicyBodyData.ntlm_enabled = true;
            }
        }

        if (parseInt(event) === POLICY_ACTION_TYPES.ALLOW.value) {
            newPolicyBodyData.risk_enabled = false;
        }

        newPolicyBodyData.retrospective_validation = RETROSPECTIVE_VALIDATION_OPTIONS.DISABLE.value;
        newPolicyBodyData.action_type = parseInt(event);
        setPolicyBodyData(newPolicyBodyData);
        setIsDirty(true);
    };
    const onTokensAllowedChanged = (event) => {
        const newFieldValue = changeTokensAllowed(event, policyBodyData.allowed_token_types, true);
        onFieldChanged('allowed_token_types', newFieldValue);
    };

    const onSSOVendorChanged = (event) => {
        onFieldChanged('sso_vendor_id', event);
        onFieldChanged('sso_app_id', []);
    };

    const onSSOApplicationChanged = (event) => {
        onFieldChanged('sso_app_id', event);
    };

    const onIncludeAccountTypeChanged = (event, accountType) => {
        if (event.target.checked) {
            setAccountTypes((currentAccountTypes) => [...currentAccountTypes, accountType]);
        } else {
            setAccountTypes((currentAccountTypes) => currentAccountTypes.filter((type) => type !== accountType));
        }
    };

    const onIncludeAllLocalAccountsChanged = (includeAllLocalAccounts) => {
        onFieldChanged('match_all_local_accounts', includeAllLocalAccounts);
    };

    const onRiskTypeChanged = (event) => {
        if (event === RISK_POLICY_TYPE_OPTIONS.AUTHENTICATION.value) {
            onFieldChanged('risk_by_level', true);
            onFieldChanged('risk_indicators', []);
        } else if (event === RISK_POLICY_TYPE_OPTIONS.USER.value) {
            onFieldChanged('risk_by_level', false);
            onFieldChanged('risk_indicators', USER_RISK_LEVEL_OPTIONS.HIGH.riskRules);
            onFieldChanged('risk_indicators_match_all', false);
        } else if (event === RISK_POLICY_TYPE_OPTIONS.RESOURCE.value) {
            onFieldChanged('risk_by_level', false);
            onFieldChanged('risk_indicators', RESOURCE_RISK_LEVEL_OPTIONS.HIGH.riskRules);
            onFieldChanged('risk_indicators_match_all', false);
        } else if (event === RISK_POLICY_TYPE_OPTIONS.SOURCE.value) {
            onFieldChanged('risk_by_level', false);
            onFieldChanged('risk_indicators', SOURCE_RISK_LEVEL_OPTIONS.HIGH.riskRules);
            onFieldChanged('risk_indicators_match_all', false);
        }
        setRiskPolicyType(event);
    };

    const onRiskLevelChanged = (event) => {
        if (riskPolicyType === RISK_POLICY_TYPE_OPTIONS.AUTHENTICATION.value) {
            onFieldChanged('risk_level', parseInt(event));
        } else if (riskPolicyType === RISK_POLICY_TYPE_OPTIONS.USER.value) {
            onFieldChanged(
                'risk_indicators',
                event === USER_RISK_LEVEL_OPTIONS.HIGH.value
                    ? USER_RISK_LEVEL_OPTIONS.HIGH.riskRules
                    : USER_RISK_LEVEL_OPTIONS.CRITICAL.riskRules
            );
        } else if (riskPolicyType === RISK_POLICY_TYPE_OPTIONS.RESOURCE.value) {
            onFieldChanged(
                'risk_indicators',
                event === RESOURCE_RISK_LEVEL_OPTIONS.HIGH.value
                    ? RESOURCE_RISK_LEVEL_OPTIONS.HIGH.riskRules
                    : RESOURCE_RISK_LEVEL_OPTIONS.CRITICAL.riskRules
            );
        } else if (riskPolicyType === RISK_POLICY_TYPE_OPTIONS.SOURCE.value) {
            onFieldChanged(
                'risk_indicators',
                event === SOURCE_RISK_LEVEL_OPTIONS.HIGH.value
                    ? SOURCE_RISK_LEVEL_OPTIONS.HIGH.riskRules
                    : SOURCE_RISK_LEVEL_OPTIONS.CRITICAL.riskRules
            );
        } else {
            console.warn('Unknown risk policy type in onRiskLevelChanged');
        }
    };

    const convertRiskIndicatorsToOption = (riskIndicators) => {
        if (riskPolicyType === RISK_POLICY_TYPE_OPTIONS.USER.value) {
            if (riskIndicators.includes(RISK_RULES.HIGH_RISK_USER)) {
                return USER_RISK_LEVEL_OPTIONS.HIGH.value;
            }
            return USER_RISK_LEVEL_OPTIONS.CRITICAL.value;
        } else if (riskPolicyType === RISK_POLICY_TYPE_OPTIONS.RESOURCE.value) {
            if (riskIndicators.includes(RISK_RULES.HIGH_RISK_RESOURCE)) {
                return RESOURCE_RISK_LEVEL_OPTIONS.HIGH.value;
            }
            return RESOURCE_RISK_LEVEL_OPTIONS.CRITICAL.value;
        } else if (riskPolicyType === RISK_POLICY_TYPE_OPTIONS.SOURCE.value) {
            if (riskIndicators.includes(RISK_RULES.HIGH_RISK_SOURCE)) {
                return SOURCE_RISK_LEVEL_OPTIONS.HIGH.value;
            }
            return SOURCE_RISK_LEVEL_OPTIONS.CRITICAL.value;
        }
    };

    const saveUsers = (selectedEntities) => {
        setShowEntitiesSelectorModal(false);
        onFieldChanged('account_types', accountTypes);
        onFieldChanged('entities', selectedEntities.objectEntities);
    };

    const saveServersIps = (selectedIps) => {
        onFieldChanged('servers_ips', selectedIps);
        setShowServersIpsSelectorModal(false);
    };

    const saveDevicesIps = (selectedIps) => {
        onFieldChanged('hosts_ips', selectedIps);
        setShowHostsIpsSelectorModal(false);
    };

    const saveDevices = (selectedDevices) => {
        setShowHostsSelectorModal(false);
        const newPolicyBodyData = { ...policyBodyData };
        if (policyBodyData.kerberos_enabled) {
            newPolicyBodyData.hosts_ips = selectedDevices.ipEntities;
            newPolicyBodyData.unmanaged_hosts = selectedDevices.unmanagedEntities;
        }
        const newDevices = selectedDevices.objectEntities.map((host) => {
            return {
                type: host.type === AD_ENTITY_TYPES.SERVER ? AD_ENTITY_TYPES.HOST : host.type,
                guid: host.guid,
                name: host.name,
                path: host.path,
                excluded: host.excluded,
                domain_name: host.domain_name,
                is_deleted: host.is_deleted
            };
        });
        newPolicyBodyData.hosts = newDevices;
        setPolicyBodyData(newPolicyBodyData);
        setIsDirty(true);
    };

    const saveServers = (selectedServers, showUnresolvedResources = false) => {
        const newRuleValues = {
            match_all_services: false,
            services: [],
            destination_types: showUnresolvedResources ? [POLICY_SELECT_DESTINATION_OPTIONS.SERVERS] : [],
            unresolved_resources_match_all_services: true,
            unresolved_resources_services: []
        };

        const newServers = [];
        let allComputers = null;
        let unresolved = null;

        for (const server of selectedServers) {
            if (server.guid === allComputersResource.guid) {
                allComputers = server;
                continue;
            }
            if (server.guid === unresolvedResources.guid) {
                unresolved = server;
                continue;
            }
            newServers.push({
                type: server.type,
                guid: server.guid,
                name: server.name,
                path: server.path,
                excluded: server.excluded,
                domain_name: server.domain_name,
                selectedServices: server.selectedServices,
                services: server.services,
                is_deleted: server.is_deleted,
                user_with_spn: server.user_with_spn,
                match_all_services: server.match_all_services
            });
        }

        if (allComputers) {
            newRuleValues.match_all_services = allComputers.match_all_services;
            newRuleValues.services = allComputers.selectedServices.map((service) => service.service_identifier);
        }
        if (unresolved) {
            newRuleValues.unresolved_resources_match_all_services = unresolved.match_all_services;
            newRuleValues.unresolved_resources_services = unresolved.selectedServices.map(
                (service) => service.service_identifier
            );
            newRuleValues.destination_types.push(POLICY_SELECT_DESTINATION_OPTIONS.UNRESOLVED);
        }

        onFieldChanged('match_all_services', newRuleValues.match_all_services);
        onFieldChanged('services', newRuleValues.services);
        onFieldChanged('servers', newServers);
        onFieldChanged('destination_types', newRuleValues.destination_types);
        onFieldChanged(
            'unresolved_resources_match_all_services',
            newRuleValues.unresolved_resources_match_all_services
        );
        onFieldChanged('unresolved_resources_services', newRuleValues.unresolved_resources_services);

        setShowServersSelectorModal(false);
    };

    const saveDesktopDestinations = (selectedDestinations) => {
        setShowDesktopDestinationSelectorModal(false);
        const newPolicyBodyData = { ...policyBodyData };
        newPolicyBodyData.servers_ips = selectedDestinations.ipEntities;

        const newDevices = selectedDestinations.objectEntities.map((host) => {
            return {
                type: host.type === AD_ENTITY_TYPES.SERVER ? AD_ENTITY_TYPES.HOST : host.type,
                guid: host.guid,
                name: host.name,
                path: host.path,
                excluded: host.excluded,
                domain_name: host.domain_name,
                is_deleted: host.is_deleted
            };
        });
        newPolicyBodyData.servers = newDevices;
        setPolicyBodyData(newPolicyBodyData);
        setIsDirty(true);
    };

    const getApplicationsObjectEntities = () => {
        if (policyBodyData.relying_parties_enabled || policyBodyData.ping_fed_enabled) {
            return policyBodyData.relying_parties || [];
        } else if (policyBodyData.okta_enabled) {
            return policyBodyData.cloud_apps || [];
        }

        return [];
    };

    const getApplicationsSelectorType = () => {
        const searchEntitiesTypes = [];
        if (policyBodyData.relying_parties_enabled) {
            searchEntitiesTypes.push(RELYING_PARTIES_TYPES.ADFS.ID);
        } else if (policyBodyData.ping_fed_enabled) {
            searchEntitiesTypes.push(RELYING_PARTIES_TYPES.PING_FED.ID);
        } else if (policyBodyData.okta_enabled) {
            searchEntitiesTypes.push(AD_ENTITY_TYPES.OKTA);
        }

        return {
            ...SELECTOR_TYPES.APPLICATIONS,
            searchEntitiesTypes: searchEntitiesTypes
        };
    };

    const saveApplications = (selectedApps) => {
        setSelectAllApplications(selectedApps.selectAllEntities);

        const filteredAppEntities = selectedApps.objectEntities.filter(
            (app) => !selectedApps.selectAllEntities || app.excluded
        );

        if (policyBodyData.relying_parties_enabled || policyBodyData.ping_fed_enabled) {
            const newRelyingParties = [];
            for (const entity of filteredAppEntities) {
                const relyingParty = policyBodyData.relying_parties_enabled
                    ? policyConfiguration.RelyingParties[entity.guid]
                    : policyConfiguration.RelyingPartiesPingFed[entity.guid];
                if (relyingParty) {
                    newRelyingParties.push({
                        name: relyingParty.name,
                        guid: entity.guid,
                        type: entity.type,
                        excluded: entity.excluded
                    });
                }
            }
            onFieldChanged('relying_parties', newRelyingParties);
        } else if (policyBodyData.okta_enabled) {
            const newCloudApps = [];
            for (const oktaApp of filteredAppEntities) {
                newCloudApps.push({
                    name: oktaApp.name,
                    display_name: oktaApp.display_name,
                    identifier: oktaApp.identifier,
                    type: oktaApp.type,
                    guid: oktaApp.guid || oktaApp.identifier,
                    excluded: oktaApp.excluded
                });
            }
            onFieldChanged('cloud_apps', newCloudApps);
        }
        setShowApplicationSelectorModal(false);
    };

    const getAccessingFields = () => {
        let accessingChildren = <></>;
        let excludeField = null;
        let isMultiChoiceField = false;
        if (policyBodyData.ntlm_enabled || policyBodyData.kerberos_enabled || policyBodyData.desktop_enabled) {
            accessingChildren = (
                <span
                    className={`${fieldStyles.policyFieldData} ${fieldStyles.clickableFieldData}`}
                    onClick={() =>
                        !policyBodyData.desktop_enabled
                            ? setShowServersSelectorModal(true)
                            : setShowDesktopDestinationSelectorModal(true)
                    }>
                    <div className={fieldStyles.entitiesData}>{selectedServersText}</div>
                    <i className={`${fieldStyles.editEntitiesIcon} fa fa-edit`}></i>
                </span>
            );
            excludeField = (
                <Collapse in={excludedServersText !== ''}>
                    <PolicyField fieldTitle={'- Excluded'} isExcludedField={true}>
                        <span className={fieldStyles.policyFieldData}>
                            <div className={fieldStyles.entitiesData}>{excludedServersText}</div>
                        </span>
                    </PolicyField>
                </Collapse>
            );
        }
        if (policyBodyData.ldap_enabled) {
            accessingChildren = (
                <span
                    className={`${fieldStyles.policyFieldData} ${fieldStyles.clickableFieldData}`}
                    onClick={() => setShowServersIpsSelectorModal(true)}>
                    <div className={fieldStyles.entitiesData}>{selectedServersIpsText}</div>
                    <i className={`${fieldStyles.editEntitiesIcon} fa fa-edit`}></i>
                </span>
            );
            excludeField = (
                <Collapse in={excludedHostsIpsText !== ''}>
                    <PolicyField fieldTitle={'- Excluded'} isExcludedField={true}>
                        <span className={fieldStyles.policyFieldData}>
                            <div className={fieldStyles.entitiesData}>{excludedHostsIpsText}</div>
                        </span>
                    </PolicyField>
                </Collapse>
            );
        }
        if (policyBodyData.oidc_enabled) {
            accessingChildren = (
                <span className={`${fieldStyles.policyFieldData} ${fieldStyles.disabledAccessing}`}>
                    <span>Entra ID</span>
                </span>
            );
        }

        if (policyBodyData.radius_enabled) {
            isMultiChoiceField = true;
            const radiusIps = Object.values(policyConfiguration.RadiusClients).map(
                (radiusClient) => radiusClient.ipAddress
            );
            accessingChildren = (
                <PolicySelectField
                    multi
                    name="form-field-name"
                    value={(policyBodyData.radius_clients || []).map((radiusClient) => radiusClient.ip_address)}
                    onChange={onRadiusClientChange}
                    options={radiusIps.map((ipAddress) => {
                        return { label: ipAddress ? ipAddress.replace('/32', '') : '', value: ipAddress };
                    })}
                    placeholder={<span className={`${fieldStyles.selectPlaceholder}`}>All RADIUS Client IPs</span>}
                />
            );
        }

        if (policyBodyData.relying_parties_enabled || policyBodyData.ping_fed_enabled) {
            accessingChildren = (
                <span
                    className={`${fieldStyles.policyFieldData} ${fieldStyles.clickableFieldData}`}
                    onClick={() => setShowApplicationSelectorModal(true)}>
                    <div className={fieldStyles.entitiesData}>{selectedRelyingPartiesTexts}</div>
                    <i className={`${fieldStyles.editEntitiesIcon} fa fa-edit`}></i>
                </span>
            );

            excludeField = (
                <Collapse in={excludedRelyingPartiesTexts !== ''}>
                    <PolicyField fieldTitle={'- Excluded'} isExcludedField={true}>
                        <span className={fieldStyles.policyFieldData}>
                            <div className={fieldStyles.entitiesData}>{excludedRelyingPartiesTexts}</div>
                        </span>
                    </PolicyField>
                </Collapse>
            );
        }

        if (policyBodyData.okta_enabled) {
            if (!policyConfiguration.OktaPolicyByApplicationEnabled) {
                accessingChildren = (
                    <span className={`${fieldStyles.policyFieldData} ${fieldStyles.disabledAccessing}`}>
                        <span>Okta</span>
                    </span>
                );
            } else {
                accessingChildren = (
                    <span
                        className={`${fieldStyles.policyFieldData} ${fieldStyles.clickableFieldData}`}
                        onClick={() => setShowApplicationSelectorModal(true)}>
                        <div className={fieldStyles.entitiesData}>{selectedApplicationsText}</div>
                        <i className={`${fieldStyles.editEntitiesIcon} fa fa-edit`}></i>
                    </span>
                );

                excludeField = (
                    <Collapse in={excludedApplicationsText !== ''}>
                        <PolicyField fieldTitle={'- Excluded'} isExcludedField={true}>
                            <span className={fieldStyles.policyFieldData}>
                                <div className={fieldStyles.entitiesData}>{excludedApplicationsText}</div>
                            </span>
                        </PolicyField>
                    </Collapse>
                );
            }
        }

        let accessingFieldTitle = 'Destination';
        if (policyBodyData.radius_enabled) {
            accessingFieldTitle = 'RADIUS client IP';
        } else if (policyBodyData.ldap_enabled) {
            accessingFieldTitle = 'Application IP';
        }

        const accessingField = (
            <PolicyField
                fieldTitle={accessingFieldTitle}
                isAccessingField={true}
                fieldClass={isMultiChoiceField ? fieldStyles.multiChoiceField : ''}>
                {accessingChildren}
            </PolicyField>
        );
        if (excludeField) {
            return (
                <>
                    {accessingField}
                    {excludeField}
                </>
            );
        }
        return accessingField;
    };

    const getHostsText = () => {
        if (!policyBodyData.kerberos_enabled) {
            return splitAndTransformEntities(policyBodyData.hosts, entitiesToSpans);
        }
        return splitAndTransformEntities(
            [
                ...(policyBodyData.hosts || []),
                ...(policyBodyData.hosts_ips || []),
                ...(policyBodyData.unmanaged_hosts || [])
            ],
            entitiesToSpans
        );
    };

    let protectedType = 1;
    for (const protectType in ENABLED_PROTOCOLS_TO_PROTECT) {
        if (policyBodyData[protectType]) {
            protectedType = ENABLED_PROTOCOLS_TO_PROTECT[protectType].value;
            break;
        }
    }
    const showProtocolField =
        policyBodyData.kerberos_enabled || policyBodyData.ntlm_enabled || policyBodyData.ldap_enabled;

    const isTokenFixed = (tokenName) => {
        return policyBodyData.desktop_enabled && MFA_TYPES[tokenName]?.ID === MFA_TYPES.OTP.ID;
    };

    const entraIdAllowedTokensOptions = Object.values(policyConfiguration.SSOApplications)
        .filter((app) => app.source === SSO_APP_SOURCE_TYPE.MFA_TOKENS)
        .filter((app) => app.vendorId === DEFAULT_VENDOR_TO_ID.AZURE)
        .map((token) => {
            return {
                value: token.id,
                label: `${MFA_TYPES.ENTRA_ID.DISPLAY_NAME} - ${token.applicationName}`,
                disabled: false,
                tokenType: MFA_TYPES.ENTRA_ID.ID
            };
        });

    const allowedTokensOptions = entraIdAllowedTokensOptions.concat(
        getAllowedTokensOptions(
            policyBodyData.allowed_token_types,
            policyConfiguration.ConfiguredTokens,
            policyBodyData.desktop_enabled,
            policyBodyData.ping_fed_enabled,
            policyBodyData.mode_type,
            policyBodyData.radius_enabled,
            isSaas,
            externalMfaProviders
        ).map((token) => {
            return {
                ...token,
                className: isTokenFixed(token.tokenType) ? fieldStyles.fixedOption : ''
            };
        })
    );

    const options = [];

    for (const riskIndicator of RISK_INDICATORS_FOR_POLICY_LIST) {
        const riskRule = RISK_INDICATORS[riskIndicator].policyRule;
        if (!policyConfiguration.hiddenRiskIndicators.includes(riskRule)) {
            options.push({
                value: riskIndicator,
                label: RISK_INDICATORS[riskIndicator].authTitle
            });
        }
    }

    const riskIndicatorsOptions = options.sort((r1, r2) => r1.label.localeCompare(r2.label));

    let protectOptions = [
        {
            ...ACTIVE_DIRECTORY_PROTECT,
            tooltip: 'Protect entities authenticating via LDAP, NTLM or Kerberos'
        },
        {
            ...ENABLED_PROTOCOLS_TO_PROTECT.oidc_enabled,
            tooltip: 'Protecting entities authenticating to applications managed by Entra ID'
        },
        {
            ...ENABLED_PROTOCOLS_TO_PROTECT.radius_enabled,
            disabled: disabledRadius,
            tooltip: 'Protect entities authenticating via RADIUS'
        },
        {
            ...ENABLED_PROTOCOLS_TO_PROTECT.relying_parties_enabled,
            disabled: disabledRelyingPartied,
            tooltip: 'Protect entities authenticating to federated applications'
        },
        {
            ...ENABLED_PROTOCOLS_TO_PROTECT.ping_fed_enabled,
            disabled: disabledRelyingPartiedPingFed,
            tooltip: 'Protect entities authenticating to PingFederate applications'
        }
    ].filter((option) => !authTypesToHide.includes(option.value));

    if (policyConfiguration.allowDesktopMfa) {
        protectOptions.push({
            ...ENABLED_PROTOCOLS_TO_PROTECT.desktop_enabled,
            tooltip: 'Protect entities authenticating to windows logon'
        });
    }

    if (policyBodyData.action_type === POLICY_ACTION_TYPES.BRIDGE.value) {
        protectOptions = protectOptions.filter(
            (po) =>
                po.value !== ENABLED_PROTOCOLS_TO_PROTECT.oidc_enabled.value &&
                po.value !== ENABLED_PROTOCOLS_TO_PROTECT.desktop_enabled.value
        );
    }

    const allComputersObject = {
        ...allComputersResource,
        match_all_services: policyBodyData.match_all_services,
        selectedServices: policyBodyData.services.map((service) => {
            return { service_identifier: service };
        }),
        services: policyConfiguration.GlobalServices
    };

    const unresolvedResourcesObject = {
        ...unresolvedResources,
        match_all_services: policyBodyData.unresolved_resources_match_all_services,
        selectedServices:
            policyBodyData.unresolved_resources_services?.map((service) => {
                return { service_identifier: service };
            }) || [],
        services: policyConfiguration.GlobalServices
    };

    const usersTexts = splitAndTransformEntities(policyBodyData.entities, entitiesToSpans);
    const selectedUsersText = usersTexts.selectedEntities === '' ? 'All Users and Groups' : usersTexts.selectedEntities;
    const excludedUsersText = usersTexts.excludedEntities;

    const hostsTexts = getHostsText();
    const selectedHostsText = hostsTexts.selectedEntities === '' ? 'All Devices' : hostsTexts.selectedEntities;
    const excludedHostsText = hostsTexts.excludedEntities;

    const hostsIpsTexts = splitAndTransformEntities(policyBodyData.hosts_ips, entitiesToSpans);
    const selectedHostsIpsText =
        hostsIpsTexts.selectedEntities === '' ? 'All Application IPs' : hostsIpsTexts.selectedEntities;
    const excludedHostsIpsText = hostsIpsTexts.excludedEntities;

    const servers = policyBodyData.servers?.length ? [...policyBodyData.servers] : [];
    const unresolvedResourcesIncluded = policyBodyData.destination_types?.includes(
        POLICY_SELECT_DESTINATION_OPTIONS.UNRESOLVED
    );
    if (unresolvedResourcesIncluded) {
        servers.unshift(unresolvedResourcesObject);
    }
    if (servers.length === 1 && unresolvedResourcesIncluded) {
        servers.unshift(allComputersObject);
    }
    const serversTexts = splitAndTransformEntities(servers, entitiesToSpans);
    const selectedServersText = serversTexts.selectedEntities === '' ? 'All resources' : serversTexts.selectedEntities;
    const excludedServersText = serversTexts.excludedEntities;

    const serverIpsTexts = splitAndTransformEntities(policyBodyData.servers_ips, entitiesToSpans);
    const selectedServersIpsText =
        serverIpsTexts.selectedEntities === '' ? 'All Application IPs' : serverIpsTexts.selectedEntities;

    const relyingPartiesTexts = splitAndTransformEntities(policyBodyData.relying_parties, entitiesToSpans);
    const selectedRelyingPartiesTexts =
        relyingPartiesTexts.selectedEntities === '' && selectAllApplications
            ? 'All Applications'
            : relyingPartiesTexts.selectedEntities;
    const excludedRelyingPartiesTexts = relyingPartiesTexts.excludedEntities;

    const applicationsTexts = splitAndTransformEntities(policyBodyData.cloud_apps, entitiesToSpans);
    const selectedApplicationsText =
        applicationsTexts.selectedEntities === '' ? 'All Applications' : applicationsTexts.selectedEntities;
    const excludedApplicationsText = applicationsTexts.excludedEntities;

    let allowedTokenTypes = policyBodyData.allowed_token_types;
    const customMfaIndex = allowedTokenTypes.indexOf(MFA_TYPES.CUSTOM_MFA.ID);
    if (customMfaIndex >= 0) {
        allowedTokenTypes = [MFA_TYPES.CUSTOM_MFA.ID];
    }

    const policyId = policyBodyData.id >= 0 ? policyBodyData.id : 'New policy';

    const riskPolicyDisplay = policyBodyData.risk_by_level
        ? RISK_BY_TYPES.LEVEL.value
        : riskPolicyType === RISK_POLICY_TYPE_OPTIONS.USER.value ||
          riskPolicyType === RISK_POLICY_TYPE_OPTIONS.RESOURCE.value ||
          riskPolicyType === RISK_POLICY_TYPE_OPTIONS.SOURCE.value
        ? RISK_BY_TYPES.LEVEL.value
        : RISK_BY_TYPES.INDICATORS.value;

    useEffect(() => {
        const isRiskTypeDisplayed = policyBodyData.risk_enabled && riskPolicyDisplay === RISK_BY_TYPES.LEVEL.value;
        const riskEntity = isRiskTypeDisplayed ? riskPolicyType : undefined;

        onFieldChanged('risk_entity', riskEntity, { shouldSetIsDirty: false });
    }, [riskPolicyType, policyBodyData.risk_enabled, riskPolicyDisplay]);

    const doAnyIncludedServerExist = policyBodyData?.servers?.some((server) => !server.excluded);
    const selectedServers = [];
    if (!doAnyIncludedServerExist) {
        selectedServers.push(allComputersObject);
    }
    if (policyBodyData.destination_types?.includes(POLICY_SELECT_DESTINATION_OPTIONS.UNRESOLVED)) {
        selectedServers.push(unresolvedResourcesObject);
    }
    if (policyBodyData?.servers?.length) {
        selectedServers.push(...policyBodyData.servers);
    }

    const actionsOptions = getActionsOptions(pricingModulesState, protectedType);

    return (
        <>
            <div className={styles.policyBodyData}>
                <div className={styles.policyFieldsContainer}>
                    <PolicyField fieldTitle={'Policy name'}>
                        <input
                            id={'policyName' + policyBodyData.id}
                            className={`${fieldStyles.policyFieldData} ${fieldStyles.inputField}`}
                            value={policyBodyData.name}
                            type="text"
                            onChange={(event) => {
                                onFieldChanged('name', event.target.value);
                            }}
                        />
                    </PolicyField>
                    <PolicyField
                        fieldTitle={'Auth type'}
                        fieldClass={fieldStyles.protectField}
                        tooltipText={
                            disabledRadius || disabledRelyingPartied || disabledRelyingPartiedPingFed
                                ? `In order to enable ${disabledRadius ? 'Radius' : ''} ${
                                      disabledRadius && disabledRelyingPartied ? 'and ' : ''
                                  } ${disabledRelyingPartied ? 'ADFS' : ''} ${
                                      disabledRadius || disabledRelyingPartied ? 'and ' : ''
                                  } ${disabledRelyingPartiedPingFed ? 'PingFederate' : ''} please configure in settings`
                                : ''
                        }>
                        <div className={fieldStyles.policyFieldData}>
                            <div className={fieldStyles.radioFieldData}>
                                <CircleRadio
                                    className={fieldStyles.radioFieldData}
                                    handleCircleRadioChange={onProtectedTypeChange}
                                    valuesList={protectOptions}
                                    radioValue={protectedType}
                                />
                            </div>
                        </div>
                    </PolicyField>
                    <Collapse in={showProtocolField}>
                        <PolicyField fieldTitle={'Protocol'}>
                            <div className={fieldStyles.policyFieldData}>
                                <div className={`${fieldStyles.protocolFieldData} ${fieldStyles.radioFieldData}`}>
                                    <div onClick={(event) => onProtocolChange(event, 'kerberos')}>
                                        <CircleRadio
                                            handleCircleRadioChange={() => {}}
                                            valuesList={[{ value: true, label: 'Kerberos' }]}
                                            radioValue={policyBodyData.kerberos_enabled}
                                        />
                                    </div>
                                    <div onClick={(event) => onProtocolChange(event, 'ntlm')}>
                                        <CircleRadio
                                            handleCircleRadioChange={() => {}}
                                            valuesList={[{ value: true, label: 'NTLM' }]}
                                            radioValue={policyBodyData.ntlm_enabled}
                                        />
                                    </div>
                                </div>
                                <div
                                    className={`${fieldStyles.protocolFieldData} ${fieldStyles.radioFieldData}`}
                                    onClick={(event) => onProtocolChange(event, 'ldap')}>
                                    <CircleRadio
                                        handleCircleRadioChange={() => {}}
                                        valuesList={[{ value: true, label: 'LDAP(s)' }]}
                                        radioValue={policyBodyData.ldap_enabled}
                                    />
                                </div>
                            </div>
                        </PolicyField>
                    </Collapse>
                    <Collapse in={policyBodyData.desktop_enabled}>
                        <PolicyField fieldTitle={'Service'}>
                            <div className={fieldStyles.policyFieldData}>
                                <div className={`${fieldStyles.serviceFieldData} ${fieldStyles.radioFieldData}`}>
                                    <div onClick={(event) => onServiceChange(event, 'logon')}>
                                        <CircleRadio
                                            handleCircleRadioChange={() => {}}
                                            valuesList={[{ value: true, label: 'Logon' }]}
                                            radioValue={policyBodyData.desktop_services.includes('logon')}
                                        />
                                    </div>
                                    <div onClick={(event) => onServiceChange(event, 'unlock')}>
                                        <CircleRadio
                                            handleCircleRadioChange={() => {}}
                                            valuesList={[{ value: true, label: 'Unlock' }]}
                                            radioValue={policyBodyData.desktop_services.includes('unlock')}
                                        />
                                    </div>
                                    <div onClick={(event) => onServiceChange(event, 'uac')}>
                                        <CircleRadio
                                            handleCircleRadioChange={() => {}}
                                            valuesList={[
                                                {
                                                    value: true,
                                                    label: 'UAC',
                                                    tooltip: 'Run as administrator is supported'
                                                }
                                            ]}
                                            radioValue={policyBodyData.desktop_services.includes('uac')}
                                        />
                                    </div>
                                </div>
                            </div>
                        </PolicyField>
                    </Collapse>

                    <PolicyField
                        fieldTitle={'Policy type'}
                        tooltipText={
                            'Static policies are applied regardless of the authentication risk level; Risk-based policies are applied when the authentication risk exceeds the selected level.'
                        }>
                        <SquareRadio
                            className={fieldStyles.policyFieldData}
                            handleSquareRadioChange={onPolicyTypeChange}
                            valuesList={getPolicyTypeOptions(pricingModulesState, policyBodyData)}
                            radioValue={
                                policyBodyData.risk_enabled ? POLICY_TYPES.RISK_BASED.value : POLICY_TYPES.STATIC.value
                            }
                        />
                    </PolicyField>
                    <Collapse in={policyBodyData.risk_enabled}>
                        <PolicyField fieldClass={`${fieldStyles.multiChoiceField} min-w-max`}>
                            <CircleRadio
                                className={fieldStyles.radioFieldData}
                                handleCircleRadioChange={onRiskTypeChange}
                                valuesList={[
                                    { value: 1, label: 'By' },
                                    { value: 2, label: 'By risk indicators' }
                                ]}
                                radioValue={riskPolicyDisplay}
                                displayGrid={true}
                            />
                            <div
                                className={`${styles.riskOptions} ${
                                    riskPolicyDisplay === RISK_BY_TYPES.LEVEL.value ? '' : styles.low
                                }`}>
                                {riskPolicyDisplay === RISK_BY_TYPES.LEVEL.value && (
                                    <div className="flex flex-row items-center max-w-full">
                                        <PolicySelectField
                                            className={`${fieldStyles.multiSelectData} min-w-[140px]`}
                                            name="form-data-name"
                                            value={riskPolicyType}
                                            options={Object.values(RISK_POLICY_TYPE_OPTIONS)}
                                            onChange={onRiskTypeChanged}
                                        />
                                        <div className="w-[100px] px-1">with risk level</div>
                                        <PolicySelectField
                                            className={`${fieldStyles.multiSelectData}`}
                                            name="form-field-name"
                                            value={
                                                riskPolicyType === RISK_POLICY_TYPE_OPTIONS.AUTHENTICATION.value
                                                    ? policyBodyData.risk_level
                                                    : convertRiskIndicatorsToOption(policyBodyData.risk_indicators)
                                            }
                                            onChange={onRiskLevelChanged}
                                            options={
                                                riskPolicyType === RISK_POLICY_TYPE_OPTIONS.USER.value
                                                    ? Object.values(USER_RISK_LEVEL_OPTIONS)
                                                    : riskPolicyType === RISK_POLICY_TYPE_OPTIONS.RESOURCE.value
                                                    ? Object.values(RESOURCE_RISK_LEVEL_OPTIONS)
                                                    : riskPolicyType === RISK_POLICY_TYPE_OPTIONS.SOURCE.value
                                                    ? Object.values(SOURCE_RISK_LEVEL_OPTIONS)
                                                    : AUTHENTICATION_RISK_LEVEL_OPTIONS
                                            }
                                        />
                                    </div>
                                )}
                                {riskPolicyDisplay === RISK_BY_TYPES.INDICATORS.value && (
                                    <PolicySelectField
                                        className={`${fieldStyles.multiSelectData}`}
                                        name="form-field-name"
                                        value={
                                            policyBodyData.risk_indicators_match_all
                                                ? RISK_INDICATORS_SELECTION_TYPE.ALL.value
                                                : RISK_INDICATORS_SELECTION_TYPE.ANY.value
                                        }
                                        onChange={(event) =>
                                            onFieldChanged(
                                                'risk_indicators_match_all',
                                                parseInt(event) === RISK_INDICATORS_SELECTION_TYPE.ALL.value
                                            )
                                        }
                                        options={Object.values(RISK_INDICATORS_SELECTION_TYPE)}
                                    />
                                )}
                            </div>
                        </PolicyField>
                    </Collapse>
                    <Collapse in={policyBodyData.risk_enabled && riskPolicyDisplay === RISK_BY_TYPES.INDICATORS.value}>
                        <PolicyField
                            fieldTitle={'Risk indicators'}
                            tooltipText={
                                'This policy will be applied if any or all of the selected Risk Indicators are identified in the authentication request.'
                            }
                            fieldClass={fieldStyles.multiChoiceField}>
                            <PolicySelectField
                                multi
                                searchable
                                className={`${fieldStyles.multiSelectData} ${
                                    policyBodyData.risk_by_level ? fieldStyles.disabled : ''
                                }`}
                                disabled={policyBodyData.risk_by_level}
                                name="form-field-name"
                                value={policyBodyData.risk_indicators}
                                onChange={onRiskIndicatorsChanged}
                                options={riskIndicatorsOptions}
                            />
                        </PolicyField>
                    </Collapse>
                    <Collapse in={policyBodyData.desktop_enabled}>
                        <PolicyField fieldTitle={'Mode type'}>
                            <PolicySelectField
                                name="form-field-name"
                                value={
                                    policyBodyData.risk_enabled
                                        ? POLICY_MODE_TYPES.ONLINE.value
                                        : policyBodyData.mode_type
                                }
                                onChange={onModeTypeChange}
                                options={
                                    policyBodyData.risk_enabled
                                        ? [POLICY_MODE_TYPES.ONLINE]
                                        : Object.values(POLICY_MODE_TYPES)
                                }
                            />
                        </PolicyField>
                    </Collapse>

                    <div className="flex justify-content-start items-center">
                        <PolicyField fieldTitle={'Users and groups'}>
                            <span
                                className={`${fieldStyles.policyFieldData} ${fieldStyles.clickableFieldData}`}
                                onClick={() => setShowEntitiesSelectorModal(true)}>
                                <div className={fieldStyles.entitiesData}>{selectedUsersText}</div>
                                <i className={`${fieldStyles.editEntitiesIcon} fa fa-edit`}></i>
                            </span>
                        </PolicyField>
                    </div>
                    <Collapse in={excludedUsersText !== ''}>
                        <PolicyField fieldTitle={'- Excluded'} isExcludedField={true}>
                            <span className={fieldStyles.policyFieldData}>
                                <div className={fieldStyles.entitiesData}>{excludedUsersText}</div>
                            </span>
                        </PolicyField>
                    </Collapse>

                    <Collapse in={policyBodyData.kerberos_enabled || policyBodyData.ntlm_enabled}>
                        <PolicyField fieldTitle={'Source'}>
                            <span
                                className={`${fieldStyles.policyFieldData} ${fieldStyles.clickableFieldData}`}
                                onClick={() => setShowHostsSelectorModal(true)}>
                                <div className={fieldStyles.entitiesData}>{selectedHostsText}</div>
                                <i className={`${fieldStyles.editEntitiesIcon} fa fa-edit`}></i>
                            </span>
                        </PolicyField>
                        <Collapse in={excludedHostsText !== ''}>
                            <PolicyField fieldTitle={'- Excluded'} isExcludedField={true}>
                                <span className={fieldStyles.policyFieldData}>
                                    <div className={fieldStyles.entitiesData}>{excludedHostsText}</div>
                                </span>
                            </PolicyField>
                        </Collapse>
                    </Collapse>
                    <Collapse in={policyBodyData.oidc_enabled || policyBodyData.okta_enabled}>
                        <PolicyField fieldTitle={'Source'}>
                            <span
                                className={`${fieldStyles.policyFieldData} ${fieldStyles.clickableFieldData}`}
                                onClick={() => setShowHostsIpsSelectorModal(true)}>
                                <div className={fieldStyles.entitiesData}>{selectedHostsIpsText}</div>
                                <i className={`${fieldStyles.editEntitiesIcon} fa fa-edit`}></i>
                            </span>
                        </PolicyField>
                        <Collapse in={excludedHostsIpsText !== ''}>
                            <PolicyField fieldTitle={'- Excluded'} isExcludedField={true}>
                                <span className={fieldStyles.policyFieldData}>
                                    <div className={fieldStyles.entitiesData}>{excludedHostsIpsText}</div>
                                </span>
                            </PolicyField>
                        </Collapse>
                    </Collapse>

                    {getAccessingFields()}

                    <PolicyField fieldTitle={'Action'}>
                        <SquareRadio
                            className={fieldStyles.policyFieldData}
                            handleSquareRadioChange={onActionTypeChange}
                            valuesList={actionsOptions}
                            radioValue={policyBodyData.action_type}
                        />
                    </PolicyField>

                    <Collapse
                        in={
                            policyBodyData.action_type === POLICY_ACTION_TYPES.MFA.value ||
                            policyBodyData.action_type === POLICY_ACTION_TYPES.BRIDGE.value
                        }>
                        <PolicyField
                            fieldTitle={'MFA prompt display name'}
                            tooltipText={
                                'The prompt text that will appear in the MFA message on the MFA token device or Silverfort’s desktop application.'
                            }>
                            <input
                                id={'resourceName' + policyBodyData.id}
                                className={`${fieldStyles.policyFieldData} ${fieldStyles.inputField}`}
                                value={policyBodyData.display_name}
                                type="text"
                                onChange={(event) => {
                                    onFieldChanged('display_name', event.target.value);
                                }}
                            />
                        </PolicyField>

                        <Collapse in={policyBodyData.action_type === POLICY_ACTION_TYPES.MFA.value}>
                            <PolicyField fieldTitle={'Tokens'} fieldClass={fieldStyles.multiChoiceField}>
                                <PolicySelectField
                                    multi
                                    name="form-field-name"
                                    value={allowedTokenTypes}
                                    onChange={onTokensAllowedChanged}
                                    options={allowedTokensOptions}
                                />
                            </PolicyField>
                        </Collapse>
                    </Collapse>

                    <Collapse in={policyBodyData.action_type === POLICY_ACTION_TYPES.BRIDGE.value}>
                        <PolicyField fieldTitle={'IdP'}>
                            <PolicySelectField
                                name="form-field-name"
                                value={policyBodyData.sso_vendor_id}
                                onChange={onSSOVendorChanged}
                                options={Object.keys(policyConfiguration.SSOVendors)
                                    .filter((vendorId) => Number(vendorId) !== DEFAULT_VENDOR_TO_ID.DEFAULT)
                                    .map((vendorId) => ({
                                        value: Number(vendorId),
                                        label: policyConfiguration.SSOVendors[vendorId].name
                                    }))}
                            />
                        </PolicyField>

                        <PolicyField fieldTitle={'Enterprise app'}>
                            <PolicySelectField
                                name="form-field-name"
                                value={policyBodyData.sso_app_id}
                                onChange={onSSOApplicationChanged}
                                options={Object.values(policyConfiguration.SSOApplications)
                                    .filter((app) => Number(app.vendorId) === Number(policyBodyData['sso_vendor_id']))
                                    .filter((app) => app.source === SSO_APP_SOURCE_TYPE.INTEGRATIONS)
                                    .map((app) => ({
                                        value: app.id,
                                        label: app.applicationName
                                    }))}
                            />
                        </PolicyField>
                    </Collapse>

                    <div className={styles.advancedOptionsButton} onClick={() => toggleShowAdvancedOptions()}>
                        Advanced Options
                    </div>
                </div>
                <div className={styles.rightBodySection}>Policy ID: {policyId}</div>
            </div>

            {showEntitiesSelectorModal && (
                <ADEntitiesSelector
                    selectedObjectEntities={policyBodyData.entities || []}
                    selectorType={getPolicyEntitiesModelSelectorType(policyBodyData)}
                    showModal={showEntitiesSelectorModal}
                    handleCloseModal={() => {
                        setAccountTypes(policyBodyData?.account_types);
                        setShowEntitiesSelectorModal(false);
                    }}
                    saveEntities={saveUsers}
                    modalTitle={'Protect Users And Groups'}
                    showExcluded={true}
                    isEditor={isEditor}
                    authTypes={
                        isS4wlAuthType ? [DIRECTORY_TYPES.ad.key, DIRECTORY_TYPES.local.key] : [DIRECTORY_TYPES.ad.key]
                    }
                    footerComponent={
                        <IncludingUsersCheckboxes
                            policyId={policyId}
                            accountTypes={accountTypes}
                            withLocalAccounts={isS4wlAuthType}
                            includeAllLocalAccounts={
                                isS4wlAuthType ? policyBodyData.match_all_local_accounts : undefined
                            }
                            onIncludeAccountTypeChanged={onIncludeAccountTypeChanged}
                            onIncludeAllLocalAccountsChanged={onIncludeAllLocalAccountsChanged}
                        />
                    }
                />
            )}

            {showHostsSelectorModal && (
                <ADEntitiesSelector
                    selectedObjectEntities={policyBodyData.hosts || []}
                    selectedIpEntities={policyBodyData.hosts_ips || []}
                    selectedUnmanagedEntities={policyBodyData.unmanaged_hosts || []}
                    selectorType={SELECTOR_TYPES.DEVICES}
                    showModal={showHostsSelectorModal}
                    handleCloseModal={() => setShowHostsSelectorModal(false)}
                    saveEntities={saveDevices}
                    showExcluded={true}
                    allowIps={policyBodyData.kerberos_enabled}
                    allowUnmanagedEntities={policyBodyData.kerberos_enabled}
                    isEditor={isEditor}
                />
            )}

            {showDesktopDestinationSelectorModal && (
                <ADEntitiesSelector
                    selectedObjectEntities={policyBodyData.servers || []}
                    selectedIpEntities={policyBodyData.servers_ips || []}
                    modalTitle={'Select Computers'}
                    selectorType={SELECTOR_TYPES.DEVICES}
                    showModal={showDesktopDestinationSelectorModal}
                    handleCloseModal={() => setShowDesktopDestinationSelectorModal(false)}
                    saveEntities={saveDesktopDestinations}
                    showExcluded={true}
                    allowIps={false}
                    allowUnmanagedEntities={false}
                    isEditor={isEditor}
                />
            )}

            {showHostsIpsSelectorModal && (
                <IpsSelector
                    selectedIps={policyBodyData.hosts_ips || []}
                    showModal={showHostsIpsSelectorModal}
                    handleCloseModal={() => setShowHostsIpsSelectorModal(false)}
                    saveIps={saveDevicesIps}
                    showExcluded={true}
                    isEditor={isEditor}
                />
            )}

            {showServersSelectorModal && (
                <ADServersSelector
                    selectedEntities={selectedServers}
                    showModal={showServersSelectorModal}
                    handleCloseModal={() => setShowServersSelectorModal(false)}
                    saveEntities={saveServers}
                    showExcluded={true}
                    GlobalServices={policyConfiguration.GlobalServices}
                    isEditor={isEditor}
                    allComputersObject={allComputersObject}
                    unresolvedResourcesObject={unresolvedResourcesObject}
                    showUnresolvedResources={policyBodyData.kerberos_enabled || policyBodyData.ntlm_enabled}
                />
            )}

            {showServersIpsSelectorModal && (
                <IpsSelector
                    selectedIps={policyBodyData.servers_ips || []}
                    showModal={showServersIpsSelectorModal}
                    handleCloseModal={() => setShowServersIpsSelectorModal(false)}
                    saveIps={saveServersIps}
                    showExcluded={false}
                    isEditor={isEditor}
                />
            )}

            {showApplicationSelectorModal && (
                <ADEntitiesSelector
                    selectedObjectEntities={getApplicationsObjectEntities()}
                    modalTitle={'Select Applications'}
                    selectorType={getApplicationsSelectorType()}
                    showModal={showApplicationSelectorModal}
                    handleCloseModal={() => setShowApplicationSelectorModal(false)}
                    saveEntities={saveApplications}
                    showExcluded={true}
                    showSelectAll={true}
                    allowSelectAll={true}
                    allEntitiesSelected={selectAllApplications}
                    selectAllText={'applications'}
                    allowIps={false}
                    allowUnmanagedEntities={false}
                    isEditor={isEditor}
                />
            )}
        </>
    );
};

export default PolicyRowMainBody;
